import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Mic, Square, Sparkles, PhoneCall, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { audioExt } from "@/lib/meetingUpload";

function Bubble({ m, mine }) {
  if (m.kind === "call") return (
    <div className="text-center my-2" data-testid={`chat-msg-${m.id}`}>
      <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-400 px-4 py-2 text-sm font-semibold"><PhoneCall className="w-4 h-4" /> {m.text} · Rejoindre</a>
    </div>
  );
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#2563EB] text-white rounded-br-md" : "klt-glow-card rounded-bl-md"}`}>
        {!mine && <p className="text-[11px] font-bold text-[#60A5FA]">{m.authorName}</p>}
        <p className="text-sm whitespace-pre-wrap">{m.kind === "voice" && <Mic className="w-3.5 h-3.5 inline mr-1 opacity-70" />}{m.text}</p>
        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{new Date(m.createdAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</p>
      </div>
    </div>
  );
}

function SummarySheet({ s, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center p-4" data-testid="chat-summary">
      <div className="w-full max-w-md klt-glow-card rounded-3xl p-6 space-y-3 text-sm relative max-h-[80vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400" data-testid="chat-summary-close"><X className="w-5 h-5" /></button>
        <p className="font-heading text-lg font-extrabold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#60A5FA]" /> Synthèse des dernières 24 h</p>
        <p>{s.overview}</p>
        {[["keyPoints", "Points clés"], ["decisions", "Décisions"]].map(([k, l]) => s[k]?.length > 0 && <div key={k}><p className="font-semibold">{l}</p>{s[k].map((x, i) => <p key={i} className="text-muted-foreground">• {x}</p>)}</div>)}
        {s.todo?.length > 0 && <div><p className="font-semibold">À faire</p>{s.todo.map((t, i) => <p key={i} className="text-muted-foreground">• <b>{t.who}</b> : {t.what}</p>)}</div>}
      </div>
    </div>
  );
}

export default function TeamChatPage() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const end = useRef(null);
  const last = useRef(null);

  useEffect(() => {
    const merge = (arr) => { if (!arr.length) return; last.current = arr[arr.length - 1].createdAt; setMsgs((m) => { const ids = new Set(m.map((x) => x.id)); return [...m, ...arr.filter((x) => !ids.has(x.id))]; }); };
    api.get("/chat/messages").then(({ data }) => merge(data)).catch(() => {});
    const t = setInterval(() => { api.get("/chat/messages", { params: last.current ? { after: last.current } : {} }).then(({ data }) => merge(data)).catch(() => {}); }, 4000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const push = (d) => { last.current = d.createdAt; setMsgs((m) => [...m, d]); };
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try { const { data } = await api.post("/chat/messages", { text: t }); push(data); } catch { toast.error("Envoi impossible"); setText(t); }
  };
  const toggleVoice = async () => {
    if (rec) { rec.stop(); setRec(null); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        setBusy(true);
        try { const fd = new FormData(); fd.append("file", blob, `voice.${audioExt(blob.type)}`); const { data } = await api.post("/chat/voice", fd); push(data); }
        catch (e) { toast.error(e.response?.data?.detail || "Message vocal impossible"); } finally { setBusy(false); }
      };
      mr.start(); setRec(mr);
    } catch { toast.error("Micro inaccessible"); }
  };
  const summarize = async () => {
    setBusy(true);
    try { const { data } = await api.post("/chat/summary"); setSummary(data); } catch { toast.error("Synthèse impossible"); } finally { setBusy(false); }
  };
  const call = async () => {
    const w = window.open("", "_blank");
    try { const { data } = await api.post("/chat/call"); push(data); if (w) w.location.href = data.url; else window.location.href = data.url; } catch { w?.close(); toast.error("Appel impossible"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex flex-col h-[calc(100dvh-9rem)] md:h-[calc(100dvh-5rem)]" data-testid="team-chat-page">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h1 className="font-heading text-2xl font-extrabold">Équipe</h1>
        <div className="flex gap-2">
          <button onClick={summarize} disabled={busy} data-testid="chat-summary-btn" className="h-9 px-3 rounded-full bg-[#3B82F6]/15 text-[#60A5FA] text-xs font-semibold inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Synthèse</button>
          <button onClick={call} data-testid="chat-call-btn" className="h-9 px-3 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold inline-flex items-center gap-1.5"><PhoneCall className="w-3.5 h-3.5" /> Appel vocal</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="chat-messages">
        {msgs.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">Lancez la discussion avec votre équipe.</p>}
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.authorId === user?.id} />)}
        <div ref={end} />
      </div>
      <div className="flex items-center gap-2 pt-3">
        <button onClick={toggleVoice} disabled={busy && !rec} data-testid="chat-voice-btn" aria-label="Message vocal" className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${rec ? "bg-rose-500 klt-rec-pulse text-white" : "bg-secondary"}`}>
          {busy && !rec ? <Loader2 className="w-4 h-4 animate-spin" /> : rec ? <Square className="w-4 h-4" fill="currentColor" /> : <Mic className="w-4 h-4" />}
        </button>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={rec ? "Enregistrement… appuyez pour envoyer" : "Écrire à l'équipe…"} data-testid="chat-input"
          className="flex-1 h-11 rounded-full bg-secondary/60 px-4 text-sm outline-none focus:ring-2 focus:ring-[#3B82F6]/50" />
        <button onClick={send} data-testid="chat-send-btn" aria-label="Envoyer" className="w-11 h-11 rounded-full klt-fab-neon text-white flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
      </div>
      {summary && <SummarySheet s={summary} onClose={() => setSummary(null)} />}
    </div>
  );
}
